// NOTABENE SDK — type-only import, erased at runtime. Keep it that way: this
// module is deliberately dependency-free so `notabeneAgentSections.test.ts`
// can assert the live agent-section policy without booting the SDK, the RTK
// store, MUI or Amplitude (which is what makes rendering the full
// NotabeneHostedMount component untestable under react-scripts/jest today).
import type { AgentSections } from "@notabene/javascript-sdk";

/**
 * The three SafeConnect surfaces we mount. Each maps to a real SDK factory —
 * see `NotabeneHostedMount` for the factory calls and per-kind required props.
 */
export type NotabeneHostedMountKind =
  | "withdrawal-assist"
  | "connect-wallet"
  | "deposit-assist";

/**
 * Explicit agent-section layout per mount kind. Supplying `agentSections`
 * REPLACES the widget's built-in layout — anything not listed is disabled —
 * so every option we still want must appear here.
 *
 *   - self-declaration in MAIN on every kind: hiding it behind the
 *     "Can't find what you're looking for?" fallback toggle made it
 *     effectively undiscoverable, and users without live wallet access
 *     (hardware wallet elsewhere, watch-only setups) were stranded on the
 *     signature step.
 *   - `deposit-assist` MAIN is self-declaration ONLY (owner call, ahead of
 *     the TR_INBOUND_HOLD rollout). Inbound is the one direction where the
 *     customer routinely cannot sign for the sending wallet at all — funds
 *     arriving from an exchange withdrawal leave them nothing to connect —
 *     so offering `signature` alongside it just invites the dead end the
 *     bullet above describes, on the flow least able to recover from it.
 *     `signature` stays in `fallback` rather than being deleted: the
 *     customers who *can* sign still reach the stronger proof, they just
 *     have to ask for it. Note a single-option MAIN gets the SDK's
 *     dedicated single-option layout (see `AgentSections` docs), so whether
 *     the fallback toggle renders at all here is Notabene's call — either
 *     way MAIN is self-declaration alone, which is the requirement.
 *   - `screenshot` / `microtransfer` intentionally excluded: micro-transfer
 *     needs a `proofs.microTransfer` destination we don't operate, and
 *     screenshot proofs feed a manual-review queue we don't run.
 *   - `add-vasp` kept on the transfer kinds (the SDK filters it out of
 *     self-hosted flows automatically).
 *   - KNOWN GAP: Notabene's hosted `/connect` page (the connect-wallet
 *     modal) ignores `agentSections` entirely — verified against their
 *     deployed bundles 2026-07-17, re-verified 2026-07-20 (the /connect
 *     route node never imports the agent-selection component that calls
 *     resolveAgentSections; only /withdrawal-assist, /deposit-assist and
 *     /counterparty-assist wire it up). The connect-wallet entry below is
 *     correct-but-inert until Notabene implements it; raised with
 *     Notabene.
 *
 * Changes here are a compliance-visible policy change, not a UI tweak —
 * `notabeneAgentSections.test.ts` pins the current policy so an edit has to
 * be deliberate.
 *
 * ── This policy is duplicated, on purpose ─────────────────────────────────────
 * The emailed manual-link (broker-initiated) flow builds its hosted URL on the
 * server and needs the same policy, so a second copy lives at
 * `apps/backend/src/app/integrations/services/notabene-agent-sections.ts`
 * (keyed by transfer direction rather than mount kind). The two cannot share an
 * import: `@notabene/javascript-sdk` is a frontend-only dependency, and CRA's
 * module scope stops this app reaching into `libs/`.
 *
 * **Change one, change the other** — otherwise the emailed link and the
 * in-portal widget offer customers different ownership-proof options for the
 * same transfer. Be honest about what the tests catch: each side's suite pins
 * its own copy, so they stop an edit whose author forgot the test next to it —
 * they do NOT stop a diligent edit that updates one file and its own test
 * together. Cross-file parity rests on this comment and review, not a machine
 * check.
 */
export const AGENT_SECTIONS: Record<NotabeneHostedMountKind, AgentSections> = {
  "withdrawal-assist": {
    main: ["signature", "self-declaration"],
    fallback: ["add-vasp"],
  },
  "deposit-assist": {
    main: ["self-declaration"],
    fallback: ["signature", "add-vasp"],
  },
  "connect-wallet": {
    main: ["signature", "self-declaration"],
    fallback: [],
  },
};
